import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslate, TFnType } from '@tolgee/react';
import { numDisplay, percentDisplay, sortByDate } from 'views/Dashboard/ImplementData/utils';
import { useSize } from 'ahooks';
import { initEcharts, EChartsOption, applyChartTheme } from 'lib/echarts';
import dayjs from 'lib/dayjs';
import styled from '@emotion/styled';
import { Col, message, Row, Space } from 'antd';
import Empty from 'components/Empty';
import { sumBy } from 'lodash-es';

interface Props {
  value?: Dashboard.RobotReceptionDaily[];
}
const Container = styled.div`
  width: 100%;
  height: 100%;
  .ant-row {
    color: var(--fett-text-secondary);
    padding-bottom: 12px;
  }
`;
const Relative = styled.div`
  position: relative;
  width: 100%;
  min-height: 340px;
  .ant-empty {
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
  }
  .bar {
    width: 100%;
    height: 340px;
  }
`;
let selectedLegendIndex: boolean[] = [true, true, true, false, false, false, false, false];

const barOption = (value: Dashboard.RobotReceptionDaily[], t: TFnType): EChartsOption => {
  const data = value;
  const dateRange = data?.map((x) => x.date).map((x) => dayjs(x).format('MM-DD'));
  const dataZoom = [
    {
      realtime: true,
      start: 0,
      end: 100,
      show: dateRange.length > 10,
    },
    {
      type: 'inside',
      realtime: true,
      start: 0,
      end: 100,
      show: dateRange.length > 10,
    },
  ];
  const legends = [
    t('ViewDashboard.会话总量'),
    t('ViewDashboard.回复会话总数'),
    t('ViewDashboard.回复率'),
    t('ViewDashboard.回复条数'),
    t('ViewDashboard.咨询转化买家数'),
    t('ViewDashboard.咨询转化率'),
    t('ViewDashboard.转化订单数'),
  ];
  return {
    grid: {
      left: '6%',
      right: '6%',
    },
    dataZoom,
    legend: {
      data: legends,
      selected: legends.reduce((pre, cur, index) => {
        return {
          ...pre,
          [cur]: selectedLegendIndex[index],
        };
      }, {}),
    },

    xAxis: {
      type: 'category',
      boundaryGap: true,
      data: dateRange,
    },

    yAxis: [
      {
        type: 'value',
        name: '',
      },
      {
        type: 'value',
        name: '%',
      },
    ],
    tooltip: {
      trigger: 'axis',
    },
    series: [
      {
        data: data.map((x) => x.会话总量 || 0),
        type: 'bar',
        name: t('ViewDashboard.会话总量'),
        tooltip: {
          valueFormatter: (value) => `${value}`,
        },
        itemStyle: {
          color: '#5B8FF9',
        },
      },
      {
        data: data.map((x) => x.回复会话总数 || 0),
        type: 'bar',
        name: t('ViewDashboard.回复会话总数'),
        tooltip: {
          valueFormatter: (value) => `${value}`,
        },
        itemStyle: {
          color: '#5AD8A6',
        },
      },
      {
        data: data.map((x) => (x.回复率 || 0) * 100),
        type: 'line',
        name: t('ViewDashboard.回复率'),
        yAxisIndex: 1,
        smooth: true,
        tooltip: {
          valueFormatter: (value) => `${percentDisplay(Number(value) / 100)}`,
        },
        lineStyle: {
          color: '#f6903d',
          opacity: 0.85,
        },
        itemStyle: {
          color: '#f6903d',
        },
      },
      {
        data: data.map((x) => x.回复条数 || 0),
        type: 'bar',
        tooltip: {
          valueFormatter: (value) => `${value}`,
        },
        name: t('ViewDashboard.回复条数'),
      },
      {
        data: data.map((x) => x.咨询转化买家数 || 0),
        type: 'bar',
        tooltip: {
          valueFormatter: (value) => `${value}`,
        },
        name: t('ViewDashboard.咨询转化买家数'),
      },
      {
        data: data.map((x) => (x.咨询转化率 || 0) * 100),
        type: 'line',
        smooth: true,
        yAxisIndex: 1,
        tooltip: {
          valueFormatter: (value) => `${percentDisplay(Number(value) / 100)}`,
        },
        name: t('ViewDashboard.咨询转化率'),
      },
      {
        data: data.map((x) => x.转化订单数 || 0),
        type: 'bar',
        tooltip: {
          valueFormatter: (value) => `${value}`,
        },
        name: t('ViewDashboard.转化订单数'),
      },
    ],
  };
};

const maxSelectedLength = 4;

const ReceptionChart = (props: Props) => {
  const { value } = props;
  const { t } = useTranslate();
  const [selectLegendLength, setSelectLegendLength] = useState(
    selectedLegendIndex.filter(Boolean).length,
  );

  const chartDomRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<echarts.ECharts>();
  const { width } = useSize(chartDomRef) || {};

  useEffect(() => {
    if (!value || !chartDomRef.current) {
      return () => {};
    }
    if (!chartRef.current) {
      chartRef.current = initEcharts(chartDomRef.current);
    }
    const chartIns = chartRef.current;
    const val = sortByDate(value) || [];
    const options = barOption(val || [], t);
    chartIns.setOption(applyChartTheme(options));
    const onSelectedChange = (params: any) => {
      const data = params as { name: string; selected: Record<string, boolean> };
      const { name, selected } = data;
      const len = Object.values(selected).filter(Boolean).length;
      selectedLegendIndex = Object.values(selected);
      if (len > 4) {
        chartIns.dispatchAction({
          type: 'legendUnSelect',
          name,
        });
        message.warn(t('ViewDashboard.您同时只可以选择x个数据。', { x: maxSelectedLength }));
      } else {
        setSelectLegendLength(len);
      }
    };
    chartIns.on('legendselectchanged', onSelectedChange);
    return () => {
      chartIns.off('legendselectchanged', onSelectedChange);
    };
  }, [value, t]);
  const stat = useMemo(() => {
    if (!value || !value.length) {
      return;
    }
    const totalReplayCount = sumBy(value, '回复条数');
    const totalReception = sumBy(value, '回复会话总数');
    return {
      count: totalReplayCount / totalReception,
      totalReception,
    };
  }, [value]);
  useEffect(() => {
    chartRef.current?.resize();
  }, [width]);

  return (
    <Container>
      <Row justify="space-between">
        <Col>
          <Space size={16}>
            <span>
              {t('ViewDashboard.平均沟通次数')}: {numDisplay(stat?.count)}
            </span>
            <span>
              {t('ViewDashboard.回复会话总数')}: {numDisplay(stat?.totalReception)}
            </span>
          </Space>
        </Col>
        <Col>
          {t('ViewDashboard.数据已选择x', { count: selectLegendLength, max: maxSelectedLength })}
        </Col>
      </Row>
      <Relative>
        {!value?.length ? <Empty /> : null}

        <div ref={chartDomRef} className="bar" />
      </Relative>
    </Container>
  );
};

export default ReceptionChart;
